
import { Component, Vue, Prop } from 'vue-property-decorator';
import { ReplenishmentSettingHistory } from '@/models/replenishment_setting_history';
import { EventBus } from '@/main';

@Component
export default class DataSyncStatusBanner extends Vue {
  @Prop({ default: 30000 })
  private pollingIntervalMs!: number;

  @Prop({ default: 10 })
  private maxPollingAttempts!: number;

  @Prop({ default: 'Your reorder settings are being applied...' })
  private message!: string;

  @Prop({ default: 'This usually takes a few minutes. Your data will refresh automatically once it\'s ready.' })
  private description!: string;

  private showWarning = false;
  private isPolling = false;
  private pollingInterval: number | null = null;
  private pollingAttempts = 0;
  private isStatusCheckInProgress = false;
  private wasActive = false;

  async created() {
    await this.checkStatusAndStartPolling();
  }

  private async checkStatusAndStartPolling(): Promise<void> {
    try {
      const history = new ReplenishmentSettingHistory();
      const status = await history.getLastSettingStatus();

      if (['PROCESSING', 'PENDING', 'RUNNING'].includes(status)) {
        this.wasActive = true;
        this.showWarning = true;
        this.$emit('status-change', status);
        this.startPolling();
      } else if (status === 'DONE' || status === 'COMPLETED') {
        this.showWarning = false;
        this.$emit('status-change', status);
        // Only emit data-ready if we previously saw an active state (avoid duplicate fetch on mount)
        if (this.wasActive) {
          this.wasActive = false;
          this.$emit('data-ready');
        }
      } else if (status === 'FAILED' || status === 'ERROR') {
        this.showWarning = false;
        this.$emit('status-change', status);
        EventBus.$emit('show-snackbar',
          'Failed to process product data. Please try again or contact support.',
          'error',
          8000
        );
      } else {
        this.showWarning = false;
        this.$emit('status-change', status);
      }
    } catch (error) {
      // If status check fails, don't show warning
      this.showWarning = false;
    }
  }

  private startPolling(): void {
    if (this.isPolling) {
      this.stopPolling();
    }

    this.isPolling = true;
    this.pollingAttempts = 0;
    this.showWarning = true;

    this.pollingInterval = window.setInterval(async () => {
      this.pollingAttempts++;

      if (this.pollingAttempts >= this.maxPollingAttempts) {
        this.stopPolling();
        this.showWarning = true; // Keep banner visible with retry button
        EventBus.$emit('show-snackbar',
          'Processing is taking longer than expected. Please refresh manually or check back later.',
          'warning',
          8000
        );
        return;
      }

      await this.checkStatus();
    }, this.pollingIntervalMs);
  }

  private async checkStatus(): Promise<void> {
    if (this.isStatusCheckInProgress) {
      return;
    }
    this.isStatusCheckInProgress = true;
    try {
      const history = new ReplenishmentSettingHistory();
      const status = await history.getLastSettingStatus();

      this.$emit('status-change', status);

      if (status === 'DONE' || status === 'COMPLETED') {
        this.stopPolling();
        this.showWarning = false;
        this.$emit('data-ready');

        EventBus.$emit('show-snackbar',
          'Product data is now ready!',
          'success',
          5000
        );
      } else if (['PROCESSING', 'PENDING', 'RUNNING'].includes(status)) {
        this.showWarning = true;
      } else if (status === 'FAILED' || status === 'ERROR') {
        this.stopPolling();
        this.showWarning = false;
        EventBus.$emit('show-snackbar',
          'Failed to process product data. Please try again or contact support.',
          'error',
          8000
        );
      }
    } catch (error) {
      console.error('Error polling status:', error);
    } finally {
      this.isStatusCheckInProgress = false;
    }
  }

  private stopPolling(): void {
    if (this.pollingInterval !== null) {
      window.clearInterval(this.pollingInterval);
      this.pollingInterval = null;
    }
    this.isPolling = false;
    this.pollingAttempts = 0;
  }

  public beforeDestroy(): void {
    this.stopPolling();
  }
}
